/**
 * ShortLinkResolver — handles `/c/:shortId` URLs (QR-scanned share links).
 *
 * A short id is the tail of an oracle_save id (e.g. save `orsv_abc123` → `abc123`).
 * We call the backend `/api/c/:shortId/resolve` for the full save id, then
 * bounce the browser to `/weekly#item-<full_id>` so the reader lands on the
 * correlation. Bots crawling `/c/xxx` never reach this component — they hit
 * the backend `/api/c/xxx` route directly, which serves OG-tagged HTML.
 */
import React, { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { api } from "../lib/api";

export default function ShortLinkResolver() {
  const { shortId } = useParams();
  const [target, setTarget] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!shortId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/c/${encodeURIComponent(shortId)}/resolve`);
        if (cancelled) return;
        if (data?.id) {
          setTarget(`/weekly#item-${data.id}`);
        } else {
          setFailed(true);
        }
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => { cancelled = true; };
  }, [shortId]);

  if (target) return <Navigate to={target} replace />;
  if (failed) {
    return (
      <div className="max-w-xl mx-auto px-6 py-40 text-center" data-testid="short-link-not-found">
        <div className="label mb-4 text-muted-foreground">Not found</div>
        <h1 className="serif text-3xl text-foreground mb-4">
          That correlation isn&apos;t here anymore.
        </h1>
        <p className="serif italic text-muted-foreground">
          It may have been unpublished — try the <a href="/weekly" className="text-[hsl(var(--primary))] underline">Weekly feed</a>.
        </p>
      </div>
    );
  }
  return (
    <div className="max-w-xl mx-auto px-6 py-40 text-center" data-testid="short-link-loading">
      <p className="serif italic text-muted-foreground">Opening the correlation…</p>
    </div>
  );
}
